import React from "react";
import moment from "moment-timezone";
import { Tag, Tooltip } from "antd";

const NZ_TIMEZONE = "Pacific/Auckland";
const toNZTime = (date: any) => date ? moment.utc(date).tz(NZ_TIMEZONE) : null;

/**
 * 和 material-table 的 columns 结构一致
 * CommonTablePage 会把它直接传给 CommonTable 使用
 */
const colorByChecklist = (s?: string) => {
  switch ((s || "").toLowerCase()) {
    case "not started":
      return "default";
    case "in progress":
      return "processing";
    case "completed":
      return "success";
    case "overdue":
      return "error";
    default:
      return "default";
  }
};

const ellipsis = (text?: string, width = 240) => (
  <Tooltip title={text}>
    <span
      style={{
        maxWidth: width,
        display: "inline-block",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      }}
    >
      {text || "-"}
    </span>
  </Tooltip>
);

export default function TaskListColumnModel() {
  return [
    {
      title: "Title",
      field: "title",
      width: 180,
      render: (row: any) => ellipsis(row.title, 180),
    },
    {
      title: "Description",
      field: "description",
      width: 200,
      render: (row: any) => ellipsis(row.description, 200),
    },
    {
      title: "Manager",
      field: "managerName",
      width: 140,
      render: (row: any) => {
        const managerName = row.managerName || row.manager || "-";
        const confirmedUserIds: number[] = row.confirmedUserIds || [];
        
        // 优先使用 managerId，如果没有则从 participants 中查找
        let managerUserId = row.managerId || row.ManagerId;
        if (!managerUserId) {
          const managerParticipant = row.participants?.find(
            (p: any) => p.role === "manager" && p.name === managerName
          );
          managerUserId = managerParticipant?.userId;
        }
        
        // 如果 Manager 的 UserId 在已确认用户列表中，则显示为绿色
        const shouldHighlight = managerUserId && confirmedUserIds.includes(managerUserId);
        
        if (shouldHighlight) {
          return (
            <Tooltip title={managerName}>
              <span
                style={{
                  maxWidth: 140,
                  display: "inline-block",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  color: "#52c41a",
                  fontWeight: 600,
                }}
              >
                {managerName}
              </span>
            </Tooltip>
          );
        }
        return ellipsis(managerName, 140);
      },
    },
    {
      title: "Assignees",
      field: "assignees",
      width: 150,
      render: (row: any) => {
        const list: string[] = row.assignees || [];
        const confirmedUserIds: number[] = row.confirmedUserIds || [];
        const participants = row.participants || [];
        
        // 为每个名字找到对应的 userId，并检查是否已确认
        const highlightedList = list.map((name) => {
          // 从 participants 中找到对应的 userId
          const participant = participants.find(
            (p: any) => p.role === "assignee" && p.name === name
          );
          const userId = participant?.userId;
          
          // 如果该用户已确认，则显示为绿色
          if (userId && confirmedUserIds.includes(userId)) {
            return (
              <span key={name} style={{ color: "#52c41a", fontWeight: 600 }}>
                {name}
              </span>
            );
          }
          return name;
        });
        
        // 检查是否有任何已确认的用户
        const hasConfirmed = list.some((name) => {
          const participant = participants.find(
            (p: any) => p.role === "assignee" && p.name === name
          );
          return participant?.userId && confirmedUserIds.includes(participant.userId);
        });
        
        if (hasConfirmed) {
          return (
            <Tooltip title={list.join(", ")}>
              <span
                style={{
                  maxWidth: 150,
                  display: "inline-block",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {highlightedList.map((item, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && ", "}
                    {item}
                  </React.Fragment>
                ))}
              </span>
            </Tooltip>
          );
        }
        return ellipsis(list.join(", "), 150);
      },
    },
    {
      title: "Followers",
      field: "followers",
      width: 150,
      render: (row: any) => {
        const list: string[] = row.followers || [];
        const confirmedUserIds: number[] = row.confirmedUserIds || [];
        const participants = row.participants || [];
        
        // 为每个名字找到对应的 userId，并检查是否已确认
        const highlightedList = list.map((name) => {
          // 从 participants 中找到对应的 userId
          const participant = participants.find(
            (p: any) => p.role === "follower" && p.name === name
          );
          const userId = participant?.userId;
          
          // 如果该用户已确认，则显示为绿色
          if (userId && confirmedUserIds.includes(userId)) {
            return (
              <span key={name} style={{ color: "#52c41a", fontWeight: 600 }}>
                {name}
              </span>
            );
          }
          return name;
        });
        
        // 检查是否有任何已确认的用户
        const hasConfirmed = list.some((name) => {
          const participant = participants.find(
            (p: any) => p.role === "follower" && p.name === name
          );
          return participant?.userId && confirmedUserIds.includes(participant.userId);
        });
        
        if (hasConfirmed) {
          return (
            <Tooltip title={list.join(", ")}>
              <span
                style={{
                  maxWidth: 150,
                  display: "inline-block",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {highlightedList.map((item, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && ", "}
                    {item}
                  </React.Fragment>
                ))}
              </span>
            </Tooltip>
          );
        }
        return ellipsis(list.join(", "), 150);
      },
    },
    {
      title: "Start Date",
      field: "startDate",
      width: 120,
      render: (row: any) =>
        row.startDate ? toNZTime(row.startDate)?.format("YYYY/MM/DD") : "-",
    },
    {
      title: "Due Date",
      field: "dueDate",
      width: 120,
      render: (row: any) =>
        row.dueDate ? toNZTime(row.dueDate)?.format("YYYY/MM/DD") : "-",
    },
    {
      title: "Status",
      field: "status",
      width: 140,
      render: (rowData: any) => (
        <Tag color={rowData.statusColor || "#8c8c8c"}>
          {rowData.status || "-"}
        </Tag>
      ),
    },
    {
      title: "Comments",
      field: "latestComment",
      width: 200,
      // 只显示最新一条的 type + content，hover 看全量
      render: (row: any) => {
        const cms = row.comments || [];
        if (!cms.length) return "-";
        const latest = cms[0]; // 接口排好序：最新在前
        return ellipsis(`${latest.commentType}: ${latest.content}`, 200);
      },
    },
  ];
}
